import { browser } from '$app/environment';

export function getDayKey(date = new Date()) {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');

	return `${year}-${month}-${day}`;
}

export function parseDayKey(dayKey: string | null | undefined): Date | null {
	if (!dayKey) return null;
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayKey);
	if (!match) return null;
	const [, year, month, day] = match;
	const date = new Date(Number(year), Number(month) - 1, Number(day));
	if (Number.isNaN(date.getTime())) return null;
	if (date.getTime() > new Date().getTime()) return null;
	return date;
}

export function getDayNumber(date = new Date()) {
	return Math.floor(new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime() / 86400000);
}

export function getDailyIndex(length: number, date = new Date()) {
	if (length <= 0) {
		throw new Error('Cannot get daily index from an empty collection.');
	}

	return getDayNumber(date) % length;
}

export function getShortDayLabel(date = new Date()) {
	return new Intl.DateTimeFormat('en-US', {
		month: 'short',
		day: 'numeric'
	}).format(date);
}

export function getFullDayLabel(date = new Date()) {
	return new Intl.DateTimeFormat('en-US', {
		weekday: 'long',
		month: 'long',
		day: 'numeric'
	}).format(date);
}

const LEADERS_SEEN_KEY = 'bubble-games-leaders-seen-day';

export function getLeadersSeenDay(): string {
	if (!browser) return '';
	try {
		return window.localStorage.getItem(LEADERS_SEEN_KEY) ?? '';
	} catch {
		return '';
	}
}

export function markLeadersSeen(dayKey = getDayKey()) {
	if (!browser) return;
	try {
		window.localStorage.setItem(LEADERS_SEEN_KEY, dayKey);
	} catch {
		// ignore storage failures (private mode, quota)
	}
}

export function watchDayRollover(onRollover: (dayKey: string) => void, intervalMs = 60000) {
	if (!browser) return () => {};

	let activeDayKey = getDayKey();

	const checkDayKey = () => {
		const nextDayKey = getDayKey();

		if (nextDayKey === activeDayKey) return;

		activeDayKey = nextDayKey;
		onRollover(nextDayKey);
	};

	const handleVisibilityChange = () => {
		if (document.visibilityState !== 'visible') return;
		checkDayKey();
	};

	window.addEventListener('focus', checkDayKey);
	document.addEventListener('visibilitychange', handleVisibilityChange);

	const intervalId = window.setInterval(checkDayKey, intervalMs);

	return () => {
		window.removeEventListener('focus', checkDayKey);
		document.removeEventListener('visibilitychange', handleVisibilityChange);
		window.clearInterval(intervalId);
	};
}
